/** Shared court-link browser helper protocol (Hera UI + Chrome extension). */

export const HERA_COURT_LINK_HASH_PARAM = "heraCourtLink";
export const HERA_COURT_LINK_PAYLOAD_VERSION = 1;
/** Session write the 1.0.3 helper reads when the hash has no form_fills. */
export const HERA_COURT_LINK_FORM_SESSION_KEY = "heraCourtLinkForm";

export type BrowserHelperSessionEntry = {
  key: string;
  template: unknown;
};

export type BrowserHelperFormFill = {
  selector: string;
  template: unknown;
  strip_leading_zeros?: boolean;
};

export type BrowserHelperConfig = {
  navigate_url_template: string;
  session_storage: BrowserHelperSessionEntry[];
  form_fills?: BrowserHelperFormFill[];
  submit_selector?: string;
  activate_selector?: string;
  activate_selectors?: string[];
  result_selector?: string;
};

export type CourtLinkHelperPayload = {
  v: number;
  writes: Array<{ key: string; value: unknown }>;
  navigate: string;
  form_fills?: Array<{
    selector: string;
    value: unknown;
    strip_leading_zeros?: boolean;
  }>;
  submit_selector?: string;
  activate_selector?: string;
  activate_selectors?: string[];
  result_selector?: string;
};

function bytesToBase64Url(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  const base64 =
    typeof btoa === "function"
      ? btoa(binary)
      : Buffer.from(binary, "binary").toString("base64");
  return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

function base64UrlToBytes(value: string): Uint8Array {
  const padded = value.replace(/-/g, "+").replace(/_/g, "/");
  const padLength = (4 - (padded.length % 4)) % 4;
  const base64 = padded + "=".repeat(padLength);
  const binary =
    typeof atob === "function"
      ? atob(base64)
      : Buffer.from(base64, "base64").toString("binary");
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/** Encode a court-link helper payload for `#heraCourtLink=…`. */
export function encodeCourtLinkHelperPayload(
  payload: CourtLinkHelperPayload,
): string {
  const json = JSON.stringify(payload);
  return bytesToBase64Url(new TextEncoder().encode(json));
}

/** Decode a court-link helper payload; returns null when invalid. */
export function decodeCourtLinkHelperPayload(
  encoded: string,
): CourtLinkHelperPayload | null {
  try {
    const json = new TextDecoder().decode(base64UrlToBytes(encoded));
    const parsed = JSON.parse(json) as CourtLinkHelperPayload;
    if (
      !parsed ||
      parsed.v !== HERA_COURT_LINK_PAYLOAD_VERSION ||
      !Array.isArray(parsed.writes) ||
      typeof parsed.navigate !== "string" ||
      !parsed.navigate.trim()
    ) {
      return null;
    }
    const hasWrites = parsed.writes.length > 0;
    const hasFormFills =
      Array.isArray(parsed.form_fills) && parsed.form_fills.length > 0;
    if (!hasWrites && !hasFormFills) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Deep-substitute ``{{name}}`` placeholders in string leaves of a JSON-like
 * template. Non-string values are copied through unchanged.
 *
 * Supports ``{{name|pad:6}}`` to left-pad a capture/value with zeros.
 */
export function substituteBrowserHelperTemplate(
  template: unknown,
  vars: Record<string, string>,
): unknown {
  if (typeof template === "string") {
    return template.replace(
      /\{\{\s*([^{}|]+?)(?:\|pad:(\d+))?\s*\}\}/g,
      (_match, rawName: string, padWidth?: string) => {
        const name = rawName.trim();
        let raw = name in vars ? (vars[name] ?? "") : "";
        if (padWidth) {
          const width = Number.parseInt(padWidth, 10);
          if (Number.isFinite(width) && width > 0) {
            raw = raw.padStart(width, "0");
          }
        }
        return raw;
      },
    );
  }
  if (Array.isArray(template)) {
    return template.map((item) => substituteBrowserHelperTemplate(item, vars));
  }
  if (template && typeof template === "object") {
    const out: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(
      template as Record<string, unknown>,
    )) {
      out[key] = substituteBrowserHelperTemplate(value, vars);
    }
    return out;
  }
  return template;
}

export function isBrowserHelperConfig(
  value: unknown,
): value is BrowserHelperConfig {
  if (!value || typeof value !== "object") {
    return false;
  }
  const config = value as BrowserHelperConfig;
  if (
    typeof config.navigate_url_template !== "string" ||
    !config.navigate_url_template.trim() ||
    !Array.isArray(config.session_storage)
  ) {
    return false;
  }
  const hasSession = config.session_storage.length > 0;
  const hasFormFills =
    Array.isArray(config.form_fills) && config.form_fills.length > 0;
  return hasSession || hasFormFills;
}

/**
 * Hillsborough compact case numbers as stored in Hera (`2026CA007806`).
 *
 * Group 1 is the 2-digit year, 2 the court code, 3 the six-digit sequence, so
 * HOVER `{{1|pad:2}}-{{2}}-{{3|pad:6}}` becomes `26-CA-007806`.
 */
export const HILLSBOROUGH_HOVER_VALUE_EXTRACT_REGEX =
  "^\\d{2}(\\d{2})([A-Za-z]{2})(\\d{6})$";

/** HOVER session object shared by search and case-summary pages. */
const HILLSBOROUGH_HOVER_SECURITY_MODEL = {
  SearchType: "ByCase",
  CaseNumber: "{{1|pad:2}}-{{2}}-{{3|pad:6}}",
  CrossReferenceNumber: "",
  CitationNumber: "",
  CaseStatus: "A",
  CaseCategory: "",
  CaseType: "",
  DateFiledFrom: "",
  DateFiledTo: "",
  CaseID: 0,
  CaptchaCode: "",
  CaptchaHash: "",
  ErrorFound: false,
  UserName: "Anonymous",
  RequestorGuid: "",
  RequestorToken: "",
};

/** Hillsborough HOVER preset for EXTENSION live links. */
export const HILLSBOROUGH_HOVER_BROWSER_HELPER_PRESET: {
  url_template: string;
  browser_helper_config: BrowserHelperConfig;
  value_extract_regex: string;
} = {
  url_template: "https://hover.hillsclerk.com/html/home.html",
  value_extract_regex: HILLSBOROUGH_HOVER_VALUE_EXTRACT_REGEX,
  browser_helper_config: {
    // The helper opens the case-number form and clicks Search. Jumping
    // straight to searchResults posts a partial security model and HOVER
    // alerts Forbidden.
    navigate_url_template:
      "https://hover.hillsclerk.com/html/case/searchResults.html",
    session_storage: [
      {
        key: "SearchSecurityModel",
        template: HILLSBOROUGH_HOVER_SECURITY_MODEL,
      },
      {
        key: "LastSearchSecurityModel",
        template: HILLSBOROUGH_HOVER_SECURITY_MODEL,
      },
      {
        key: "SummarySecurityModel",
        template: HILLSBOROUGH_HOVER_SECURITY_MODEL,
      },
    ],
  },
};

function courtLinkFormSessionEntry(options: {
  selector: string;
  valueTemplate: string;
  activateSelector?: string;
  submitSelector?: string;
  resultSelector?: string;
}): BrowserHelperSessionEntry {
  return {
    key: HERA_COURT_LINK_FORM_SESSION_KEY,
    template: {
      ...(options.activateSelector
        ? { activate_selector: options.activateSelector }
        : {}),
      ...(options.submitSelector
        ? { submit_selector: options.submitSelector }
        : {}),
      ...(options.resultSelector
        ? { result_selector: options.resultSelector }
        : {}),
      form_fills: [
        { selector: options.selector, value: options.valueTemplate },
      ],
    },
  };
}

/** Broward clerk case numbers such as ``COWE2026036077`` or ``CACE15085978``. */
export const BROWARD_CASE_VALUE_EXTRACT_REGEX =
  "^([A-Za-z]{3,4})(?:20)?(\\d{2})(\\d+)$";

/** Lee Matrix ``362026CA004941`` / ``2025CA004905`` → ``26-CA-004941``. */
export const LEE_MATRIX_VALUE_EXTRACT_REGEX =
  "^(?:\\d{2})?(?:20)?(\\d{2})([A-Za-z]{2})(\\d+)$";

/** Orange myeclerk ``2026CA009360`` / ``2025CC003124O`` → ``YYYY-XX-999999-O``. */
export const ORANGE_FL_CASE_VALUE_EXTRACT_REGEX =
  "^(\\d{4})-?([A-Za-z]{2})-?(\\d+)(?:-?[A-Za-z])?$";

export const BROWARD_WEB2_BROWSER_HELPER_PRESET: {
  url_template: string;
  browser_helper_config: BrowserHelperConfig;
  value_extract_regex: string;
} = {
  url_template: "https://www.browardclerk.org/web2/",
  value_extract_regex: BROWARD_CASE_VALUE_EXTRACT_REGEX,
  browser_helper_config: {
    navigate_url_template: "https://www.browardclerk.org/web2/",
    session_storage: [
      courtLinkFormSessionEntry({
        selector: "#CaseNumber",
        valueTemplate: "{{1}}{{2}}{{3}}",
        activateSelector: 'a[href="#caseNumberSearch"]',
        submitSelector: "#CaseNumberSearchResults",
      }),
    ],
    activate_selector: 'a[href="#caseNumberSearch"]',
    submit_selector: "#CaseNumberSearchResults",
    form_fills: [
      {
        selector: "#CaseNumber",
        template: "{{1}}{{2}}{{3}}",
      },
    ],
  },
};

export const LEE_MATRIX_BROWSER_HELPER_PRESET: {
  url_template: string;
  browser_helper_config: BrowserHelperConfig;
  value_extract_regex: string;
} = {
  url_template: "https://matrix.leeclerk.org/",
  value_extract_regex: LEE_MATRIX_VALUE_EXTRACT_REGEX,
  browser_helper_config: {
    navigate_url_template: "https://matrix.leeclerk.org/",
    session_storage: [
      courtLinkFormSessionEntry({
        selector:
          "#byCaseNumber_CaseNumber, input[name='byCaseNumber.CaseNumber']",
        valueTemplate: "{{1}}-{{2}}-{{3}}",
      }),
    ],
    form_fills: [
      {
        selector:
          "#byCaseNumber_CaseNumber, input[name='byCaseNumber.CaseNumber']",
        template: "{{1}}-{{2}}-{{3}}",
      },
    ],
  },
};

/** Benchmark public search (Clay / Martin / St. Johns). */
export const FL_BENCHMARK_CASE_VALUE_EXTRACT_REGEX =
  "^(\\d{4})([A-Za-z]{2})(\\d+)$";
/** Martin ATTOM numbers such as ``25000459CA`` or ``26864CA``. */
export const MARTIN_BENCHMARK_CASE_VALUE_EXTRACT_REGEX = "^\\d+[A-Za-z]{2,}$";
/** St. Johns ``2026CA001414`` → ``CA26-1414``. */
export const ST_JOHNS_BENCHMARK_CASE_VALUE_EXTRACT_REGEX =
  "^(?:20)?(\\d{2})([A-Za-z]{2})0*(\\d+)$";
const FL_BENCHMARK_ACTIVATE_SELECTOR = 'input[searchtype="CaseNumber"]';

export function flBenchmarkBrowserHelperPreset(
  searchUrl: string,
  options: {
    valueExtractRegex?: string;
    valueTemplate?: string;
  } = {},
): {
  url_template: string;
  browser_helper_config: BrowserHelperConfig;
  value_extract_regex: string;
} {
  const valueExtractRegex =
    options.valueExtractRegex ?? FL_BENCHMARK_CASE_VALUE_EXTRACT_REGEX;
  const valueTemplate = options.valueTemplate ?? "{{1}}{{2}}{{3}}";
  return {
    url_template: searchUrl,
    value_extract_regex: valueExtractRegex,
    browser_helper_config: {
      navigate_url_template: searchUrl,
      session_storage: [
        courtLinkFormSessionEntry({
          selector: "#caseNumber",
          valueTemplate,
          activateSelector: FL_BENCHMARK_ACTIVATE_SELECTOR,
          submitSelector: "#searchButton",
        }),
      ],
      activate_selector: FL_BENCHMARK_ACTIVATE_SELECTOR,
      submit_selector: "#searchButton",
      form_fills: [
        {
          selector: "#caseNumber",
          template: valueTemplate,
        },
      ],
    },
  };
}

export const CLAY_BENCHMARK_BROWSER_HELPER_PRESET =
  flBenchmarkBrowserHelperPreset(
    "https://inquiry.clayclerk.com/Home.aspx/Search",
  );
export const MARTIN_BENCHMARK_BROWSER_HELPER_PRESET =
  flBenchmarkBrowserHelperPreset(
    "https://court.martinclerk.com/Home.aspx/Search",
    {
      valueExtractRegex: MARTIN_BENCHMARK_CASE_VALUE_EXTRACT_REGEX,
      valueTemplate: "{{value}}",
    },
  );
export const ST_JOHNS_BENCHMARK_BROWSER_HELPER_PRESET =
  flBenchmarkBrowserHelperPreset(
    "https://apps.stjohnsclerk.com/Benchmark/Home.aspx/Search",
    {
      valueExtractRegex: ST_JOHNS_BENCHMARK_CASE_VALUE_EXTRACT_REGEX,
      valueTemplate: "{{2}}{{1}}-{{3}}",
    },
  );

/** Alachua LINDAS ``2026CA000927`` / ``012025CA003027`` → year / type / seq. */
export const ALACHUA_LINDAS_CASE_VALUE_EXTRACT_REGEX =
  "^(?:01)?(\\d{4})([A-Za-z]{2})(\\d+)$";

export const ALACHUA_LINDAS_BROWSER_HELPER_PRESET: {
  url_template: string;
  browser_helper_config: BrowserHelperConfig;
  value_extract_regex: string;
} = {
  url_template: "https://www.alachuaclerk.org/court_records/",
  value_extract_regex: ALACHUA_LINDAS_CASE_VALUE_EXTRACT_REGEX,
  browser_helper_config: {
    navigate_url_template: "https://www.alachuaclerk.org/court_records/",
    session_storage: [
      {
        key: HERA_COURT_LINK_FORM_SESSION_KEY,
        template: {
          submit_selector: "button-text=Search",
          form_fills: [
            { selector: "#CaseNumberYear", value: "{{1}}" },
            { selector: "#CaseNumberType", value: "{{2}}" },
            { selector: "#CaseNumberSeq", value: "{{3|pad:6}}" },
          ],
        },
      },
    ],
    submit_selector: "button-text=Search",
    form_fills: [
      { selector: "#CaseNumberYear", template: "{{1}}" },
      { selector: "#CaseNumberType", template: "{{2}}" },
      { selector: "#CaseNumberSeq", template: "{{3|pad:6}}" },
    ],
  },
};

/** Bradford Civitek ``25000475CA`` → year 2025 / CA / 000475. */
export const BRADFORD_CIVITEK_CASE_VALUE_EXTRACT_REGEX =
  "^(?:20)?(\\d{2})(\\d+)([A-Za-z]{2})$";

export const BRADFORD_CIVITEK_BROWSER_HELPER_PRESET: {
  url_template: string;
  browser_helper_config: BrowserHelperConfig;
  value_extract_regex: string;
} = {
  url_template: "https://www.civitekflorida.com/ocrs/county/04/",
  value_extract_regex: BRADFORD_CIVITEK_CASE_VALUE_EXTRACT_REGEX,
  browser_helper_config: {
    navigate_url_template: "https://www.civitekflorida.com/ocrs/county/04/",
    session_storage: [
      {
        key: HERA_COURT_LINK_FORM_SESSION_KEY,
        template: {
          activate_selector: 'li[data-index="1"] > a',
          activate_selectors: [
            "text=Public",
            "text=I Agree",
            'li[data-index="1"] > a',
          ],
          submit_selector: "button-text=Search",
          form_fills: [
            { selector: '[id="form:search_tab:year"]', value: "20{{1}}" },
            {
              selector: '[id="form:search_tab:cs_court1_input"]',
              value: "{{3}}",
            },
            { selector: '[id="form:search_tab:seq"]', value: "{{2|pad:6}}" },
          ],
        },
      },
    ],
    activate_selector: 'li[data-index="1"] > a',
    activate_selectors: [
      "text=Public",
      "text=I Agree",
      'li[data-index="1"] > a',
    ],
    submit_selector: "button-text=Search",
    form_fills: [
      { selector: '[id="form:search_tab:year"]', template: "20{{1}}" },
      {
        selector: '[id="form:search_tab:cs_court1_input"]',
        template: "{{3}}",
      },
      { selector: '[id="form:search_tab:seq"]', template: "{{2|pad:6}}" },
    ],
  },
};

/** Brevard BECA ``2025CA043823`` → year / CA / 43823. Years after 2023 use XXCA-BC. */
export const BREVARD_BECA_CASE_VALUE_EXTRACT_REGEX =
  "^(?:05[-]?)?(\\d{4})[-]?([A-Za-z]{2})[-]?0*(\\d+)(?:[-A-Za-z].*)?$";

export const BREVARD_BECA_BROWSER_HELPER_PRESET: {
  url_template: string;
  browser_helper_config: BrowserHelperConfig;
  value_extract_regex: string;
} = {
  url_template: "https://vmatrix1.brevardclerk.us/beca/beca_splash.cfm",
  value_extract_regex: BREVARD_BECA_CASE_VALUE_EXTRACT_REGEX,
  browser_helper_config: {
    navigate_url_template:
      "https://vmatrix1.brevardclerk.us/beca/beca_splash.cfm",
    session_storage: [
      {
        key: HERA_COURT_LINK_FORM_SESSION_KEY,
        template: {
          activate_selector: "text=Case Number",
          activate_selectors: [
            "button-text=Submit",
            "button-text=General Public",
            "text=Case Number",
          ],
          submit_selector: "button-text=Search",
          form_fills: [
            { selector: 'input[name="CaseNumber1"]', value: "05" },
            { selector: 'input[name="CaseNumber2"]', value: "{{1}}" },
            { selector: 'input[name="CaseNumber3"]', value: "{{2}}" },
            {
              selector: 'input[name="CaseNumber4"]',
              value: "{{3}}",
              strip_leading_zeros: true,
            },
          ],
        },
      },
    ],
    activate_selector: "text=Case Number",
    activate_selectors: [
      "button-text=Submit",
      "button-text=General Public",
      "text=Case Number",
    ],
    submit_selector: "button-text=Search",
    form_fills: [
      { selector: 'input[name="CaseNumber1"]', template: "05" },
      { selector: 'input[name="CaseNumber2"]', template: "{{1}}" },
      { selector: 'input[name="CaseNumber3"]', template: "{{2}}" },
      {
        selector: 'input[name="CaseNumber4"]',
        template: "{{3}}",
        strip_leading_zeros: true,
      },
    ],
  },
};

export const ORANGE_FL_MYECLERK_BROWSER_HELPER_PRESET: {
  url_template: string;
  browser_helper_config: BrowserHelperConfig;
  value_extract_regex: string;
} = {
  url_template: "https://myeclerk.myorangeclerk.com/Cases/Search",
  value_extract_regex: ORANGE_FL_CASE_VALUE_EXTRACT_REGEX,
  browser_helper_config: {
    navigate_url_template: "https://myeclerk.myorangeclerk.com/Cases/Search",
    session_storage: [
      courtLinkFormSessionEntry({
        selector: "#caseNumber, input[name='CaseNumber']",
        valueTemplate: "{{1}}-{{2}}-{{3}}-O",
        submitSelector: "#caseSearch",
      }),
    ],
    submit_selector: "#caseSearch",
    form_fills: [
      {
        selector: "#caseNumber, input[name='CaseNumber']",
        template: "{{1}}-{{2}}-{{3}}-O",
      },
    ],
  },
};

/** Citrus SCORSS ``2026CA000072`` / ``2014CA123`` as stored. Captcha, then Search. */
export const CITRUS_SCORSS_BROWSER_HELPER_PRESET: {
  url_template: string;
  browser_helper_config: BrowserHelperConfig;
  value_extract_regex: string;
} = {
  url_template: "https://scorss.citrusclerk.org/",
  value_extract_regex: "",
  browser_helper_config: {
    navigate_url_template: "https://scorss.citrusclerk.org/",
    session_storage: [
      courtLinkFormSessionEntry({
        selector: "#SearchForm_casenumber",
        valueTemplate: "{{value}}",
        activateSelector: 'a[href="#casenumbertab"]',
        submitSelector: "#casenumbersubmit",
      }),
    ],
    activate_selector: 'a[href="#casenumbertab"]',
    submit_selector: "#casenumbersubmit",
    form_fills: [{ selector: "#SearchForm_casenumber", template: "{{value}}" }],
  },
};

/** Duval CORE ``2026CA006140`` / ``162011CA010595`` → year / type / sequence. */
export const DUVAL_CORE_CASE_VALUE_EXTRACT_REGEX =
  "^(?:16[-]?)?(\\d{4})[-]?([A-Za-z]{2})[-]?(\\d+)(?:[-A-Za-z].*)?$";

export const DUVAL_CORE_BROWSER_HELPER_PRESET: {
  url_template: string;
  browser_helper_config: BrowserHelperConfig;
  value_extract_regex: string;
} = {
  url_template: "https://core.duvalclerk.com/CoreCms.aspx?mode=PublicAccess",
  value_extract_regex: DUVAL_CORE_CASE_VALUE_EXTRACT_REGEX,
  browser_helper_config: {
    navigate_url_template:
      "https://core.duvalclerk.com/CoreCms.aspx?mode=PublicAccess",
    session_storage: [
      courtLinkFormSessionEntry({
        selector: 'input[id^="c_UcnEntryBox"]',
        valueTemplate: "{{1}}{{2}}{{3}}",
        activateSelector: "#c_CaseSearchItem",
        submitSelector: 'input[id^="c_SubmitCaseLookupButton"]',
      }),
    ],
    activate_selector: "#c_CaseSearchItem",
    submit_selector: 'input[id^="c_SubmitCaseLookupButton"]',
    form_fills: [
      { selector: 'input[id^="c_UcnEntryBox"]', template: "{{1}}{{2}}{{3}}" },
    ],
  },
};

/** Volusia CCMS ``202613332CIDL`` → ``2026 13332 CIDL``. Captcha stays manual. */
export const VOLUSIA_CCMS_CASE_VALUE_EXTRACT_REGEX =
  "^(\\d{4})(\\d+)([A-Za-z]{4})$";

export const VOLUSIA_CCMS_BROWSER_HELPER_PRESET: {
  url_template: string;
  browser_helper_config: BrowserHelperConfig;
  value_extract_regex: string;
} = {
  url_template: "https://app02.clerk.org/ccms/",
  value_extract_regex: VOLUSIA_CCMS_CASE_VALUE_EXTRACT_REGEX,
  browser_helper_config: {
    navigate_url_template: "https://app02.clerk.org/ccms/",
    session_storage: [
      courtLinkFormSessionEntry({
        selector: "#Content1_CaseNum",
        valueTemplate: "{{1}} {{2|pad:5}} {{3}}",
        activateSelector: "text=Accept",
      }),
    ],
    activate_selector: "text=Accept",
    form_fills: [
      {
        selector: "#Content1_CaseNum",
        template: "{{1}} {{2|pad:5}} {{3}}",
      },
    ],
  },
};

/** Cook CivilCaseSearch: paste the stored number into the single Case Number box. */
export const COOK_CIVIL_CASE_SEARCH_VALUE_EXTRACT_REGEX = "";

const COOK_CIVIL_CASE_SEARCH_URL =
  "https://casesearch.cookcountyclerkofcourt.org/CivilCaseSearchAPI.aspx";

const COOK_CIVIL_CASE_NUMBER_SELECTOR =
  '#txtCaseNumber, #CaseNumber, input[name="txtCaseNumber"], input[name="CaseNumber"], input[id*="CaseNumber" i], input[name*="CaseNumber" i], form input[type="text"], label=Case Number';

export const COOK_CIVIL_CASE_SEARCH_BROWSER_HELPER_PRESET: {
  url_template: string;
  browser_helper_config: BrowserHelperConfig;
  value_extract_regex: string;
} = {
  url_template: COOK_CIVIL_CASE_SEARCH_URL,
  value_extract_regex: COOK_CIVIL_CASE_SEARCH_VALUE_EXTRACT_REGEX,
  browser_helper_config: {
    navigate_url_template: COOK_CIVIL_CASE_SEARCH_URL,
    session_storage: [
      {
        key: HERA_COURT_LINK_FORM_SESSION_KEY,
        template: {
          form_fills: [
            {
              selector: COOK_CIVIL_CASE_NUMBER_SELECTOR,
              value: "{{value}}",
            },
          ],
        },
      },
    ],
    form_fills: [
      {
        selector: COOK_CIVIL_CASE_NUMBER_SELECTOR,
        template: "{{value}}",
      },
    ],
  },
};

/**
 * NJ eCourts ``F00815824`` / ``SWCF00706520`` → series + 2-digit year.
 * Do not paste the leading F; Foreclosure (F) is the docket type, not part of the number.
 */
export const NJ_CIVIL_SEARCH_VALUE_EXTRACT_REGEX =
  "^(?:SWC)?F-?(\\d+)-?(\\d{2})$";

const NJ_CIVIL_SEARCH_URL = "https://portalcivsearch-cloud.njcourts.gov/";

const NJ_CIVIL_SEARCH_ACTIVATE_SELECTORS = [
  "text=General Equity",
  "text=Foreclosure (F)",
];

const NJ_DOCKET_NUMBER_SELECTOR =
  'input[name*="DocketNumber" i], input[id*="DocketNumber" i], input[aria-label*="Docket Number" i], label=Docket Number';

const NJ_DOCKET_YEAR_SELECTOR =
  'input[name*="DocketYear" i], input[id*="DocketYear" i], input[aria-label*="Docket Year" i], label=Docket Year';

export const NJ_CIVIL_SEARCH_BROWSER_HELPER_PRESET: {
  url_template: string;
  browser_helper_config: BrowserHelperConfig;
  value_extract_regex: string;
} = {
  url_template: NJ_CIVIL_SEARCH_URL,
  value_extract_regex: NJ_CIVIL_SEARCH_VALUE_EXTRACT_REGEX,
  browser_helper_config: {
    navigate_url_template: NJ_CIVIL_SEARCH_URL,
    session_storage: [
      {
        key: HERA_COURT_LINK_FORM_SESSION_KEY,
        template: {
          activate_selectors: NJ_CIVIL_SEARCH_ACTIVATE_SELECTORS,
          form_fills: [
            {
              selector: NJ_DOCKET_NUMBER_SELECTOR,
              value: "{{1}}",
            },
            {
              selector: NJ_DOCKET_YEAR_SELECTOR,
              value: "{{2}}",
            },
          ],
        },
      },
    ],
    activate_selectors: NJ_CIVIL_SEARCH_ACTIVATE_SELECTORS,
    form_fills: [
      {
        selector: NJ_DOCKET_NUMBER_SELECTOR,
        template: "{{1}}",
      },
      {
        selector: NJ_DOCKET_YEAR_SELECTOR,
        template: "{{2}}",
      },
    ],
  },
};

/** Allegheny DCR ``MG26000729`` → type / 2-digit year / 6-digit sequence. */
export const ALLEGHENY_DCR_CASE_VALUE_EXTRACT_REGEX =
  "^([A-Za-z]{2})[-]?(\\d{2})[-]?(\\d+)$";

const ALLEGHENY_DCR_SEARCH_URL =
  "https://dcr.alleghenycounty.us/civil/LoginSearch.aspx";

export const ALLEGHENY_DCR_BROWSER_HELPER_PRESET: {
  url_template: string;
  browser_helper_config: BrowserHelperConfig;
  value_extract_regex: string;
} = {
  url_template: ALLEGHENY_DCR_SEARCH_URL,
  value_extract_regex: ALLEGHENY_DCR_CASE_VALUE_EXTRACT_REGEX,
  browser_helper_config: {
    navigate_url_template: ALLEGHENY_DCR_SEARCH_URL,
    session_storage: [
      {
        key: HERA_COURT_LINK_FORM_SESSION_KEY,
        template: {
          form_fills: [
            { selector: "#PublicTextField1", value: "{{1}}" },
            { selector: "#PublicTextField2", value: "{{2}}" },
            { selector: "#PublicTextField3", value: "{{3|pad:6}}" },
          ],
        },
      },
    ],
    form_fills: [
      { selector: "#PublicTextField1", template: "{{1}}" },
      { selector: "#PublicTextField2", template: "{{2}}" },
      { selector: "#PublicTextField3", template: "{{3|pad:6}}" },
    ],
  },
};

/**
 * Illinois Tyler public notices use ``2026-FC-0000107`` (Peoria, 7-digit
 * sequence) and ``2026-FC-000076`` (Tazewell, 6-digit). Anonymous Smart
 * Search blocks wildcards.
 */
export const ILLINOIS_TYLER_CASE_VALUE_EXTRACT_REGEX =
  "^(\\d{4})[-]?([A-Za-z]{2})[-]?0*(\\d+)$";

const PEORIA_TYLER_SEARCH_URL =
  "https://portal-ilpeoria.tylertech.cloud/Portal/Home/Dashboard/29";
const TAZEWELL_TYLER_SEARCH_URL =
  "https://portal-iltazewell.tylertech.cloud/Portal/Home/Dashboard/29";

function illinoisTylerSmartSearchPreset(
  url: string,
  sequencePad: 6 | 7,
): {
  url_template: string;
  browser_helper_config: BrowserHelperConfig;
  value_extract_regex: string;
} {
  const valueTemplate = `{{1}}-{{2}}-{{3|pad:${sequencePad}}}`;
  const resultSelector = ".k-grid a, a.caseLink, a[href*='Case']";
  return {
    url_template: url,
    value_extract_regex: ILLINOIS_TYLER_CASE_VALUE_EXTRACT_REGEX,
    browser_helper_config: {
      navigate_url_template: url,
      session_storage: [
        courtLinkFormSessionEntry({
          selector: "#caseCriteria_SearchCriteria",
          valueTemplate,
          resultSelector,
        }),
      ],
      result_selector: resultSelector,
      form_fills: [
        {
          selector: "#caseCriteria_SearchCriteria",
          template: valueTemplate,
        },
      ],
    },
  };
}

export const PEORIA_TYLER_BROWSER_HELPER_PRESET =
  illinoisTylerSmartSearchPreset(PEORIA_TYLER_SEARCH_URL, 7);

export const TAZEWELL_TYLER_BROWSER_HELPER_PRESET =
  illinoisTylerSmartSearchPreset(TAZEWELL_TYLER_SEARCH_URL, 6);

/**
 * NY NYSCEF guest Case Search. FCAS hides cases until an RJI is filed;
 * NYSCEF includes pre-RJI e-filed foreclosures. The case-number Search
 * button is invisible hCaptcha; clicking it is the challenge. After
 * Search, the helper clicks the matching result then Document List.
 */
const NY_NYSCEF_SEARCH_URL =
  "https://iapps.courts.state.ny.us/nyscef/CaseSearch";

const NY_NYSCEF_RESULT_SELECTOR =
  "a[href*='docketId'], a[href*='CaseDetails'], a[href*='DocumentList']";

const NY_NYSCEF_SUBMIT_SELECTOR =
  'button[name="btnSubmit"][aria-label="Search by case number search"]';

export const NY_FCAS_BROWSER_HELPER_PRESET: {
  url_template: string;
  browser_helper_config: BrowserHelperConfig;
  value_extract_regex: string;
} = {
  url_template: NY_NYSCEF_SEARCH_URL,
  value_extract_regex: "",
  browser_helper_config: {
    navigate_url_template: NY_NYSCEF_SEARCH_URL,
    session_storage: [
      courtLinkFormSessionEntry({
        selector:
          "#txtCaseIdentifierNumber, input[name='txtCaseIdentifierNumber']",
        valueTemplate: "{{value}}",
        submitSelector: NY_NYSCEF_SUBMIT_SELECTOR,
        resultSelector: NY_NYSCEF_RESULT_SELECTOR,
      }),
    ],
    submit_selector: NY_NYSCEF_SUBMIT_SELECTOR,
    result_selector: NY_NYSCEF_RESULT_SELECTOR,
    form_fills: [
      {
        selector:
          "#txtCaseIdentifierNumber, input[name='txtCaseIdentifierNumber']",
        template: "{{value}}",
      },
    ],
  },
};

/** Judici ``2026FC44`` / ``2026-FC-000044`` → unpadded ``2026FC44``. Captcha stays manual. */
export const JUDICI_CASE_VALUE_EXTRACT_REGEX =
  "^(\\d{4})[-]?([A-Za-z]{2,4})[-]?0*(\\d+)$";

const JUDICI_RESULT_SELECTOR = "a[href*='case_information'], a[href*='ocl=']";

const JUDICI_SUBMIT_SELECTOR = "#searchForm button, button[value='Search']";

export function judiciIllinoisBrowserHelperPreset(courtId: string): {
  url_template: string;
  browser_helper_config: BrowserHelperConfig;
  value_extract_regex: string;
} {
  const searchUrl = `https://www.judici.com/courts/cases/case_search.jsp?court=${courtId}`;
  const urlTemplate = `${searchUrl}&case_number={{1}}{{2}}{{3}}`;
  return {
    url_template: urlTemplate,
    value_extract_regex: JUDICI_CASE_VALUE_EXTRACT_REGEX,
    browser_helper_config: {
      navigate_url_template: urlTemplate,
      session_storage: [
        courtLinkFormSessionEntry({
          selector: "input[name='case_number']",
          valueTemplate: "{{1}}{{2}}{{3}}",
          submitSelector: JUDICI_SUBMIT_SELECTOR,
          resultSelector: JUDICI_RESULT_SELECTOR,
        }),
      ],
      submit_selector: JUDICI_SUBMIT_SELECTOR,
      result_selector: JUDICI_RESULT_SELECTOR,
      form_fills: [
        {
          selector: "input[name='case_number']",
          template: "{{1}}{{2}}{{3}}",
        },
      ],
    },
  };
}

export const ADAMS_JUDICI_BROWSER_HELPER_PRESET =
  judiciIllinoisBrowserHelperPreset("IL001025J");

/** DuPage ``2026FC0501`` → ``2026FC000501``. Captcha/bot-check stays manual. */
export const DUPAGE_CASE_VALUE_EXTRACT_REGEX =
  "^(\\d{4})[-]?([A-Za-z]{2})[-]?0*(\\d+)$";

const DUPAGE_SEARCH_URL =
  "https://epay.dupagecircuitclerk.gov/Clerk/caseNumberSearch.do";

export const DUPAGE_CASE_SEARCH_BROWSER_HELPER_PRESET: {
  url_template: string;
  browser_helper_config: BrowserHelperConfig;
  value_extract_regex: string;
} = {
  url_template: DUPAGE_SEARCH_URL,
  value_extract_regex: DUPAGE_CASE_VALUE_EXTRACT_REGEX,
  browser_helper_config: {
    navigate_url_template: DUPAGE_SEARCH_URL,
    session_storage: [
      courtLinkFormSessionEntry({
        selector: "input[name='caseNumber']",
        valueTemplate: "{{1}}{{2}}{{3|pad:6}}",
        submitSelector: "#searchCaseNumber",
      }),
    ],
    submit_selector: "#searchCaseNumber",
    form_fills: [
      {
        selector: "input[name='caseNumber']",
        template: "{{1}}{{2}}{{3|pad:6}}",
      },
    ],
  },
};

/** Northampton ATTOM ``C0048CV202603968`` → CountySuite ``C-48-CV-2026-03968``. */
export const NORTHAMPTON_COUNTYSUITE_CASE_VALUE_EXTRACT_REGEX =
  "^[Cc]-?0*48-?[Cc][Vv]-?(\\d{4})-?0*(\\d+)$";

const NORTHAMPTON_SEARCH_URL =
  "https://web.northamptoncounty.org/CountySuite.EServices/CaseSearch";

const NORTHAMPTON_CASE_TEMPLATE = "C-48-CV-{{1}}-{{2|pad:5}}";
const NORTHAMPTON_SUBMIT_SELECTOR = "#submit";
const NORTHAMPTON_RESULT_SELECTOR = "td, [role='cell'], a";
const NORTHAMPTON_ACCEPT_SELECTOR = "button-text=Accept";

export const NORTHAMPTON_COUNTYSUITE_BROWSER_HELPER_PRESET: {
  url_template: string;
  browser_helper_config: BrowserHelperConfig;
  value_extract_regex: string;
} = {
  url_template: NORTHAMPTON_SEARCH_URL,
  value_extract_regex: NORTHAMPTON_COUNTYSUITE_CASE_VALUE_EXTRACT_REGEX,
  browser_helper_config: {
    navigate_url_template: NORTHAMPTON_SEARCH_URL,
    session_storage: [
      {
        key: HERA_COURT_LINK_FORM_SESSION_KEY,
        template: {
          activate_selector: NORTHAMPTON_ACCEPT_SELECTOR,
          activate_selectors: [NORTHAMPTON_ACCEPT_SELECTOR],
          submit_selector: NORTHAMPTON_SUBMIT_SELECTOR,
          result_selector: NORTHAMPTON_RESULT_SELECTOR,
          form_fills: [
            {
              selector: "#userSearchString, input[name='userSearchString']",
              value: NORTHAMPTON_CASE_TEMPLATE,
            },
            {
              selector: "select[name='caseSearchType']",
              value: "CaseNumber",
            },
          ],
        },
      },
    ],
    activate_selector: NORTHAMPTON_ACCEPT_SELECTOR,
    activate_selectors: [NORTHAMPTON_ACCEPT_SELECTOR],
    submit_selector: NORTHAMPTON_SUBMIT_SELECTOR,
    result_selector: NORTHAMPTON_RESULT_SELECTOR,
    form_fills: [
      {
        selector: "#userSearchString, input[name='userSearchString']",
        template: NORTHAMPTON_CASE_TEMPLATE,
      },
      {
        selector: "select[name='caseSearchType']",
        template: "CaseNumber",
      },
    ],
  },
};

/** York ATTOM ``2026SU002364`` → CountySuite ``2026-SU-002364``. */
export const YORK_COUNTYSUITE_CASE_VALUE_EXTRACT_REGEX =
  "^(\\d{4})[-]?([A-Za-z]{2})[-]?0*(\\d+)$";

const YORK_SEARCH_URL = "https://prothysearchnew.yorkcountypa.gov/";
const YORK_CASE_TEMPLATE = "{{1}}-{{2}}-{{3|pad:6}}";
const YORK_SEARCH_INPUT = "input[formcontrolname='SearchText']";
const YORK_RESULT_SELECTOR = "a";
const YORK_ACTIVATE_SELECTORS = [
  "[formcontrolname='SearchFilter'] [role='combobox']",
  "text=Case Number",
];

export const YORK_COUNTYSUITE_BROWSER_HELPER_PRESET: {
  url_template: string;
  browser_helper_config: BrowserHelperConfig;
  value_extract_regex: string;
} = {
  url_template: YORK_SEARCH_URL,
  value_extract_regex: YORK_COUNTYSUITE_CASE_VALUE_EXTRACT_REGEX,
  browser_helper_config: {
    navigate_url_template: YORK_SEARCH_URL,
    session_storage: [
      {
        key: HERA_COURT_LINK_FORM_SESSION_KEY,
        template: {
          activate_selectors: YORK_ACTIVATE_SELECTORS,
          submit_selector: "key=Enter",
          result_selector: YORK_RESULT_SELECTOR,
          form_fills: [
            {
              selector: YORK_SEARCH_INPUT,
              value: YORK_CASE_TEMPLATE,
            },
          ],
        },
      },
    ],
    activate_selectors: YORK_ACTIVATE_SELECTORS,
    submit_selector: "key=Enter",
    result_selector: YORK_RESULT_SELECTOR,
    form_fills: [
      {
        selector: YORK_SEARCH_INPUT,
        template: YORK_CASE_TEMPLATE,
      },
    ],
  },
};

/** Dauphin ATTOM ``2026CV01845`` → CountySuite ``2026-CV-01845``. */
export const DAUPHIN_COUNTYSUITE_CASE_VALUE_EXTRACT_REGEX =
  "^(\\d{4})[-]?([A-Za-z]{2})[-]?(\\d+)$";

const DAUPHIN_SEARCH_URL =
  "https://publicaccess.dauphincounty.gov/civil.casesearch/";
const DAUPHIN_CASE_TEMPLATE = "{{1}}-{{2}}-{{3}}";

export const DAUPHIN_COUNTYSUITE_BROWSER_HELPER_PRESET: {
  url_template: string;
  browser_helper_config: BrowserHelperConfig;
  value_extract_regex: string;
} = {
  url_template: DAUPHIN_SEARCH_URL,
  value_extract_regex: DAUPHIN_COUNTYSUITE_CASE_VALUE_EXTRACT_REGEX,
  browser_helper_config: {
    navigate_url_template: DAUPHIN_SEARCH_URL,
    session_storage: [
      {
        key: HERA_COURT_LINK_FORM_SESSION_KEY,
        template: {
          activate_selectors: YORK_ACTIVATE_SELECTORS,
          submit_selector: "key=Enter",
          result_selector: YORK_RESULT_SELECTOR,
          form_fills: [
            {
              selector: YORK_SEARCH_INPUT,
              value: DAUPHIN_CASE_TEMPLATE,
            },
          ],
        },
      },
    ],
    activate_selectors: YORK_ACTIVATE_SELECTORS,
    submit_selector: "key=Enter",
    result_selector: YORK_RESULT_SELECTOR,
    form_fills: [
      {
        selector: YORK_SEARCH_INPUT,
        template: DAUPHIN_CASE_TEMPLATE,
      },
    ],
  },
};

/** Erie Court Pro. ``202612024`` opens ``2026-12024`` directly. */
const ERIE_PA_SEARCH_URL =
  "https://courtpro.eriecountypa.gov/Prothy/v/search/case";

export const ERIE_PA_COURTPRO_BROWSER_HELPER_PRESET: {
  url_template: string;
  browser_helper_config: BrowserHelperConfig;
  value_extract_regex: string;
} = {
  url_template: ERIE_PA_SEARCH_URL,
  value_extract_regex: "",
  browser_helper_config: {
    navigate_url_template: ERIE_PA_SEARCH_URL,
    session_storage: [
      courtLinkFormSessionEntry({
        selector: "#CaseNumber, input[name='CaseNumber']",
        valueTemplate: "{{value}}",
        submitSelector: "button-text=Search",
      }),
    ],
    submit_selector: "button-text=Search",
    form_fills: [
      {
        selector: "#CaseNumber, input[name='CaseNumber']",
        template: "{{value}}",
      },
    ],
  },
};

/** Bucks Court Pro. ``202501687`` opens ``2025-01687`` directly. */
const BUCKS_PA_SEARCH_URL =
  "https://propublic.buckscountyonline.org/PSI/v/search/case";

export const BUCKS_PA_COURTPRO_BROWSER_HELPER_PRESET: {
  url_template: string;
  browser_helper_config: BrowserHelperConfig;
  value_extract_regex: string;
} = {
  url_template: BUCKS_PA_SEARCH_URL,
  value_extract_regex: "",
  browser_helper_config: {
    navigate_url_template: BUCKS_PA_SEARCH_URL,
    session_storage: [
      courtLinkFormSessionEntry({
        selector: "#CaseNumber, input[name='CaseNumber']",
        valueTemplate: "{{value}}",
        submitSelector: "button-text=Search",
      }),
    ],
    submit_selector: "button-text=Search",
    form_fills: [
      {
        selector: "#CaseNumber, input[name='CaseNumber']",
        template: "{{value}}",
      },
    ],
  },
};

/** Montgomery Court Pro. ``2312201`` must be searched as ``2023-12201``. */
export const MONTGOMERY_PA_COURTPRO_CASE_VALUE_EXTRACT_REGEX =
  "^(?:20)?(\\d{2})-?(\\d{5})$";

const MONTGOMERY_PA_SEARCH_URL =
  "https://courtsapp.montcopa.org/psi/v/search/case";
const MONTGOMERY_PA_CASE_TEMPLATE = "20{{1}}-{{2}}";

export const MONTGOMERY_PA_COURTPRO_BROWSER_HELPER_PRESET: {
  url_template: string;
  browser_helper_config: BrowserHelperConfig;
  value_extract_regex: string;
} = {
  url_template: MONTGOMERY_PA_SEARCH_URL,
  value_extract_regex: MONTGOMERY_PA_COURTPRO_CASE_VALUE_EXTRACT_REGEX,
  browser_helper_config: {
    navigate_url_template: MONTGOMERY_PA_SEARCH_URL,
    session_storage: [
      courtLinkFormSessionEntry({
        selector: "#CaseNumber, input[name='CaseNumber']",
        valueTemplate: MONTGOMERY_PA_CASE_TEMPLATE,
        submitSelector: "button-text=Search",
      }),
    ],
    submit_selector: "button-text=Search",
    form_fills: [
      {
        selector: "#CaseNumber, input[name='CaseNumber']",
        template: MONTGOMERY_PA_CASE_TEMPLATE,
      },
    ],
  },
};

/** Westmoreland iJEMS. ``2025CJ03446`` must be searched as ``25CJ03446``. */
export const WESTMORELAND_PA_IJEMS_CASE_VALUE_EXTRACT_REGEX =
  "^(?:20)?(\\d{2})[-]?([A-Za-z]{2})[-]?(\\d+)$";

const WESTMORELAND_PA_SEARCH_URL =
  "https://prothonotary.westmorelandcountypa.gov/ijems/";
const WESTMORELAND_PA_CASE_TEMPLATE = "{{1}}{{2}}{{3}}";
const WESTMORELAND_PA_ACTIVATE_SELECTORS = [
  "text=Public Search",
  "input[type=checkbox]",
  "button-text=ACCEPT",
  "text=Search by Case #",
];

export const WESTMORELAND_PA_IJEMS_BROWSER_HELPER_PRESET: {
  url_template: string;
  browser_helper_config: BrowserHelperConfig;
  value_extract_regex: string;
} = {
  url_template: WESTMORELAND_PA_SEARCH_URL,
  value_extract_regex: WESTMORELAND_PA_IJEMS_CASE_VALUE_EXTRACT_REGEX,
  browser_helper_config: {
    navigate_url_template: WESTMORELAND_PA_SEARCH_URL,
    session_storage: [
      {
        key: HERA_COURT_LINK_FORM_SESSION_KEY,
        template: {
          activate_selectors: WESTMORELAND_PA_ACTIVATE_SELECTORS,
          submit_selector: "button-text=Search",
          result_selector: "span.spanlink",
          form_fills: [
            {
              selector: "input[name='txtcaseno']",
              value: WESTMORELAND_PA_CASE_TEMPLATE,
            },
          ],
        },
      },
    ],
    activate_selectors: WESTMORELAND_PA_ACTIVATE_SELECTORS,
    submit_selector: "button-text=Search",
    result_selector: "span.spanlink",
    form_fills: [
      {
        selector: "input[name='txtcaseno']",
        template: WESTMORELAND_PA_CASE_TEMPLATE,
      },
    ],
  },
};

/** Luzerne SearchIQS. Guest search; ``202511436`` opens the foreclosure docket. */
const LUZERNE_PA_SEARCH_URL = "https://www.searchiqs.com/paluz/";

export const LUZERNE_PA_SEARCHIQS_BROWSER_HELPER_PRESET: {
  url_template: string;
  browser_helper_config: BrowserHelperConfig;
  value_extract_regex: string;
} = {
  url_template: LUZERNE_PA_SEARCH_URL,
  value_extract_regex: "",
  browser_helper_config: {
    navigate_url_template: LUZERNE_PA_SEARCH_URL,
    session_storage: [
      courtLinkFormSessionEntry({
        selector: "#ContentPlaceHolder1_txtProthCaseNum",
        valueTemplate: "{{value}}",
        activateSelector: "button-text=Search Records as Guest",
        submitSelector: "#ContentPlaceHolder1_cmdSearch",
      }),
    ],
    activate_selector: "button-text=Search Records as Guest",
    submit_selector: "#ContentPlaceHolder1_cmdSearch",
    form_fills: [
      {
        selector: "#ContentPlaceHolder1_txtProthCaseNum",
        template: "{{value}}",
      },
    ],
  },
};

/** Hamilton OH clerk. ``A2604717`` opens Common Pleas foreclosure summary. */
const HAMILTON_OH_SEARCH_URL =
  "https://www.courtclerk.org/records-search/case-number-search/";

export const HAMILTON_OH_CASE_SEARCH_BROWSER_HELPER_PRESET: {
  url_template: string;
  browser_helper_config: BrowserHelperConfig;
  value_extract_regex: string;
} = {
  url_template: HAMILTON_OH_SEARCH_URL,
  value_extract_regex: "",
  browser_helper_config: {
    navigate_url_template: HAMILTON_OH_SEARCH_URL,
    session_storage: [
      courtLinkFormSessionEntry({
        selector: "input[name='casenumber']",
        valueTemplate: "{{value}}",
        submitSelector: "input[type=submit][value=Search]",
      }),
    ],
    submit_selector: "input[type=submit][value=Search]",
    form_fills: [
      {
        selector: "input[name='casenumber']",
        template: "{{value}}",
      },
    ],
  },
};

/** Clark OH CourtView. ``2025CV0960`` → ``25CV0960``. Image captcha, then Click Here. */
const CLARK_OH_SEARCH_URL =
  "https://eservices.clarkcountyohiocourt.com/eservices/home.page.2";

export const CLARK_OH_COURTVIEW_CASE_VALUE_EXTRACT_REGEX =
  "^(?:20)?(\\d{2})\\s*([A-Za-z]{2})\\s*(\\d+)$";

const CLARK_OH_COURTVIEW_CASE_TEMPLATE = "{{1}}{{2}}{{3}}";

const CLARK_OH_ACTIVATE_SELECTORS = ["text=Case Number"];

export const CLARK_OH_COURTVIEW_BROWSER_HELPER_PRESET: {
  url_template: string;
  browser_helper_config: BrowserHelperConfig;
  value_extract_regex: string;
} = {
  url_template: CLARK_OH_SEARCH_URL,
  value_extract_regex: CLARK_OH_COURTVIEW_CASE_VALUE_EXTRACT_REGEX,
  browser_helper_config: {
    navigate_url_template: CLARK_OH_SEARCH_URL,
    session_storage: [
      {
        key: HERA_COURT_LINK_FORM_SESSION_KEY,
        template: {
          activate_selectors: CLARK_OH_ACTIVATE_SELECTORS,
          submit_selector: "input[name=submitLink]",
          result_selector: "a[id*='cell-3$link']",
          form_fills: [
            {
              selector: "#caseDscr",
              value: CLARK_OH_COURTVIEW_CASE_TEMPLATE,
            },
          ],
        },
      },
    ],
    activate_selectors: CLARK_OH_ACTIVATE_SELECTORS,
    submit_selector: "input[name=submitLink]",
    result_selector: "a[id*='cell-3$link']",
    form_fills: [
      {
        selector: "#caseDscr",
        template: CLARK_OH_COURTVIEW_CASE_TEMPLATE,
      },
    ],
  },
};

/** Delaware PA C-Track. Guest disclaimer first; ``CV2025006370`` finds ``CV-2025-006370``. */
const DELAWARE_PA_SEARCH_URL =
  "https://delcopublicaccess.co.delaware.pa.us/search/case";

export const DELAWARE_PA_CTRACK_BROWSER_HELPER_PRESET: {
  url_template: string;
  browser_helper_config: BrowserHelperConfig;
  value_extract_regex: string;
} = {
  url_template: DELAWARE_PA_SEARCH_URL,
  value_extract_regex: "",
  browser_helper_config: {
    navigate_url_template: DELAWARE_PA_SEARCH_URL,
    session_storage: [
      {
        key: HERA_COURT_LINK_FORM_SESSION_KEY,
        template: {
          activate_selectors: [
            "button-text=Continue as Public User",
            "a[href='/search/case']",
          ],
          submit_selector: ".text-right.mt-6 button",
          result_selector: "a",
          form_fills: [
            {
              selector: ".v-field--has-background input.v-field__input",
              value: "{{value}}",
            },
          ],
        },
      },
    ],
    activate_selectors: [
      "button-text=Continue as Public User",
      "a[href='/search/case']",
    ],
    submit_selector: ".text-right.mt-6 button",
    result_selector: "a",
    form_fills: [
      {
        selector: ".v-field--has-background input.v-field__input",
        template: "{{value}}",
      },
    ],
  },
};
